import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { ConsentEnglishNotice } from '@/components/consent/ConsentEnglishNotice';
import { StevensTopbar } from '@/components/landing/StevensTopbar';
import { StevensFooter } from '@/components/landing/StevensFooter';
import { SkipToContent } from '@/components/a11y/SkipToContent';
import { useApplicationsClosed } from '@/hooks/useApplicationsClosed';
import './landing-v2.css';

const TRF_URL = 'https://www.trf.org';

// #2109 R3 L8: /welcome is public, so every word below comes from the
// `landing` namespace and follows the anonymous language picker in the hero.
// Victor 2026-10-20: the public page never names the assistant (Ember is
// retired and families pick their own persona), so no string takes {{companion}}.

/** The <em> slot every section title uses. */
const EM = { em: <em /> };

const IconCompanion = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 12a8 8 0 0 1-11.6 7.1L4 21l1.9-5.4A8 8 0 1 1 21 12Z" />
    <path d="M9 11h.01M12 11h.01M15 11h.01" />
  </svg>
);
const IconBills = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 3h14v18l-3-2-2 2-2-2-2 2-2-2-3 2V3Z" />
    <path d="M9 8h6M9 12h6M9 16h3" />
  </svg>
);

function Hero() {
  const { t } = useTranslation('landing');
  const applyClosed = useApplicationsClosed();

  return (
    <section className="hero">
      <div className="wrap hero-stack">
        <h1 className="display">
          <Trans t={t} i18nKey="hero.title" components={EM} />
        </h1>
        <p className="hero-lede">
          {t('hero.lede')}
        </p>
        <div className="hero-cta">
          {/* Victor 2026-10-20: the hero carries Apply only. Sign in, My
              account and the language switcher live in the topbar, which
              hides its own Apply here so the page has one. Apply stays in
              all auth states: /apply shows the confirm-intent interstitial
              (GH-271.1). */}
          {/* Melanie 2026-11-03: grey once the cycle has closed. It still
              opens /apply, which says the cycle is closed and who to email. */}
          <Link to="/apply" className={applyClosed ? 'btn btn-closed' : 'btn'}>
            {applyClosed ? t('hero.applyClosed') : t('hero.apply')}
          </Link>
        </div>
        <div className="stack-art">
          {/* #884: below-the-fold decorative photo — lazy + low priority, with
              width variants so a phone on hospital wifi isn't fetching the
              desktop-scale asset. Box stays reserved via the CSS
              aspect-ratio regardless of which variant loads (no CLS). */}
          <picture>
            <source
              srcSet="/hero-family-480.webp 480w, /hero-family-900.webp 900w, /hero-family-1400.webp 1400w"
              sizes="(max-width: 960px) 100vw, 900px"
              type="image/webp"
            />
            <img
              src="/hero-family-900.jpg"
              alt={t('hero.photoAlt')}
              className="stack-art-photo"
              width={900}
              height={394}
              loading="lazy"
              fetchPriority="low"
              decoding="async"
            />
          </picture>
        </div>
      </div>
    </section>
  );
}

// Illustrative requests, not quotes from real parents; each one maps to a
// tool the assistant really has (appointments, doctor questions, get_my_grants).
const INTRO_EXAMPLES = ['calendar', 'doctor', 'receipt'] as const;

function StevensIntro() {
  const { t } = useTranslation('landing');
  return (
    <section className="stevens-intro">
      <div className="wrap">
        <div className="grid">
          <div className="label">{t('intro.label')}</div>
          <div>
            <h2><Trans t={t} i18nKey="intro.title" components={EM} /></h2>
            <p className="lede">
              {t('intro.lede')}
            </p>
            <div className="examples">
              {INTRO_EXAMPLES.map((id) => (
                <div key={id} className="example-bubble">
                  <div className="who">{t(`intro.examples.${id}.who`)}</div>
                  <p>{t(`intro.examples.${id}.quote`)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const FEATURES = [
  { id: 'companion', className: 'feat-stevens', Icon: IconCompanion },
  { id: 'finances', className: 'feat-bills', Icon: IconBills },
] as const;

function Features() {
  const { t } = useTranslation('landing');
  return (
    <section id="what" className="features wrap">
      <div className="head">
        <div className="label">{t('features.label')}</div>
        <h2><Trans t={t} i18nKey="features.title" components={EM} /></h2>
      </div>
      <div className="features-grid">
        {FEATURES.map(({ id, className, Icon }) => (
          <article key={id} className={`feat ${className}`}>
            <div className="feat-top">
              <span className="num">{t(`features.${id}.num`)}</span>
              <div className="feat-icon"><Icon /></div>
            </div>
            <div>
              <h3>{t(`features.${id}.title`)}</h3>
              <p>
                {t(`features.${id}.body`)}
              </p>
            </div>
            <div className="feat-foot">
              <span className="tick">&#10003;</span>
              <span>{t(`features.${id}.foot`)}</span>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function Mission() {
  const { t } = useTranslation('landing');
  return (
    <section id="mission" className="mission">
      <div className="inner">
        <div className="label">{t('mission.label')}</div>
        <div>
          <h3>{t('mission.title')}</h3>
          <p>
            {t('mission.body')}
          </p>
        </div>
        <div className="cta-col">
          <a href={TRF_URL} target="_blank" rel="noopener noreferrer" className="btn">
            {t('mission.cta')}
          </a>
        </div>
      </div>
    </section>
  );
}

const TRUST_ITEMS = ['privacy', 'cost', 'safety'] as const;
const TRUST_LINKS = { privacy: <Link to="/privacy" className="link" /> };

function Trust() {
  const { t } = useTranslation('landing');
  return (
    <section id="trust" className="trust wrap">
      <div className="head">
        <div className="label">{t('trust.label')}</div>
        <h2><Trans t={t} i18nKey="trust.title" components={EM} /></h2>
      </div>
      {/* Honesty note (Victor 2026-10-20): every privacy sentence was
          audited against production. The page must not say "encrypted at
          rest" (the production disk and its snapshots are unencrypted and
          field encryption is off), "only TRF staff" (AI, voice, messaging
          and tracing vendors process family content), or that every staff
          view is logged (#3071). Since 2026-10-21 the tile claims only that
          what a family shares is used to help them and is never sold, and
          links the Privacy Policy for the detail. Restore stronger wording
          only after the infrastructure changes land. The copy lives in
          `landing:trust.privacy.body` and `help:parent.medical.description`. */}
      <div className="trust-grid">
        {TRUST_ITEMS.map((id) => (
          <div key={id} className="trust-item">
            <span className="k">{t(`trust.${id}.k`)}</span>
            <span className="v">{t(`trust.${id}.v`)}</span>
            <p><Trans t={t} i18nKey={`trust.${id}.body`} components={TRUST_LINKS} /></p>
          </div>
        ))}
      </div>
    </section>
  );
}

const FAQS = ['chatbot', 'invite'] as const;

function FAQ() {
  const { t } = useTranslation('landing');
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    <section id="faq" className="faq wrap">
      <div className="head">
        <div className="label">{t('faq.label')}</div>
        <h2><Trans t={t} i18nKey="faq.title" components={EM} /></h2>
      </div>
      <div className="faq-list">
        {FAQS.map((id, i) => (
          <details key={id} className="faq-item" open={i === 0}>
            <summary>
              <span className="q-num">
                {t('faq.counter', { index: pad(i + 1), total: pad(FAQS.length) })}
              </span>
              <span className="q">{t(`faq.${id}.q`)}</span>
              <span className="toggle" aria-hidden="true">+</span>
            </summary>
            <div className="a"><p>{t(`faq.${id}.a`)}</p></div>
          </details>
        ))}
      </div>
    </section>
  );
}

function SMS() {
  const { t, i18n } = useTranslation('landing');
  // Victor’s ruling 2026-09-29: the consent wording is carrier-reviewed
  // (TCPA/10DLC), so it stays English and authoritative in every locale, with
  // ConsentEnglishNotice saying so in the reader’s language. The label and
  // heading above it are chrome and follow the reader.
  const tEn = i18n.getFixedT('en', 'landing');
  return (
    <section className="sms wrap">
      <div className="grid">
        <div className="label">{t('sms.label')}</div>
        <div>
          <h3>{t('sms.title')}</h3>
          <ConsentEnglishNotice className="mb-3" />
          <p lang="en">
            {/* STOP and HELP are the carrier keywords a reply must carry, so
                they are params, never translated. */}
            <Trans
              t={tEn}
              i18nKey="sms.body"
              values={{ stop: 'STOP', help: 'HELP' }}
              components={{ strong: <strong /> }}
            />
          </p>
          <p lang="en" className="small">
            <Trans
              t={tEn}
              i18nKey="sms.small"
              components={{
                smsTerms: <Link to="/sms-terms" className="link" />,
                privacy: <Link to="/privacy" className="link" />,
              }}
            />
          </p>
        </div>
      </div>
    </section>
  );
}

export function LandingPage() {
  return (
    <div className="landing-page">
      {/* part of #882: first focusable element on the page, before the
          topbar's own nav — keyboard/switch-control/screen-reader users
          land here first and can jump straight past the nav to content. */}
      <SkipToContent />
      <StevensTopbar showApply={false} />
      <main id="main-content">
        <Hero />
        <StevensIntro />
        <Features />
        <Mission />
        <Trust />
        <FAQ />
        <SMS />
      </main>
      <StevensFooter />
    </div>
  );
}
